import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, Modal, Field, TextInput, TextArea, Select, StatusBadge } from "./adminShared";
import { Plus, Trash2, Pencil, Star } from "lucide-react";

export default function Testimonials() {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ name: "", city: "", serviceType: "Individual", quote: "", rating: 5, status: "approved", featured: false, displayOrder: 0, consentObtained: true });

  const save = async () => {
    if (edit.status === "approved" && !edit.consentObtained) { toast.error("Consent must be obtained before approving."); return; }
    try {
      if (edit.testimonial_id) await api.put(`/admin/testimonials/${edit.testimonial_id}`, edit);
      else await api.post("/admin/testimonials", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };
  const quickApprove = async (t) => {
    if (!t.consentObtained) { toast.error("Consent must be obtained first — edit to confirm consent."); return; }
    await api.put(`/admin/testimonials/${t.testimonial_id}`, { ...t, status: "approved" }); toast.success("Approved"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl text-earth" style={{ fontWeight: 600 }}>Testimonials</h1>
        <button onClick={openNew} className="btn-gold text-sm" data-testid="new-testimonial"><Plus size={16} className="mr-1" /> New</button>
      </div>
      {!items ? <p className="text-earth/50">Loading…</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((t) => (
            <Card key={t.testimonial_id} className="p-5">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={t.status} />
                  {t.featured && <span className="text-xs text-gold-dark flex items-center gap-1"><Star size={12} className="fill-gold text-gold" /> Featured</span>}
                  {!t.consentObtained && <span className="text-xs text-destructive">No consent</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEdit({ ...t })} className="p-1.5 text-earth/50 hover:text-earth" data-testid={`edit-testimonial-${t.testimonial_id}`}><Pencil size={15} /></button>
                  <button onClick={() => del(t.testimonial_id)} className="p-1.5 text-earth/50 hover:text-destructive"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-earth italic mt-3 text-sm line-clamp-3">"{t.quote}"</p>
              <p className="text-xs text-earth/50 mt-2">{t.name} · {t.serviceType}</p>
              {t.status === "pending" && <button onClick={() => quickApprove(t)} className="mt-3 text-sm font-semibold text-gold-dark" data-testid={`approve-${t.testimonial_id}`}>Approve →</button>}
            </Card>
          ))}
        </div>
      )}

      {edit && (
        <Modal title={edit.testimonial_id ? "Edit testimonial" : "New testimonial"} onClose={() => setEdit(null)}>
          <Field label="Display name" hint="e.g. R.K., Ludhiana"><TextInput value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="tst-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="City"><TextInput value={edit.city || ""} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></Field>
            <Field label="Service type"><Select value={edit.serviceType} onChange={(e) => setEdit({ ...edit, serviceType: e.target.value })}><option>Individual</option><option>Online</option><option>Workshop</option></Select></Field>
          </div>
          <Field label="Quote"><TextArea rows={3} value={edit.quote} onChange={(e) => setEdit({ ...edit, quote: e.target.value })} data-testid="tst-quote" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status"><Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="tst-status"><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></Select></Field>
            <Field label="Display order"><TextInput type="number" value={edit.displayOrder} onChange={(e) => setEdit({ ...edit, displayOrder: parseInt(e.target.value) || 0 })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-earth/80"><input type="checkbox" checked={edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} className="accent-[#C9A84C]" data-testid="tst-featured" /> Feature on homepage</label>
          <label className="flex items-center gap-2 text-sm text-earth/80"><input type="checkbox" checked={edit.consentObtained} onChange={(e) => setEdit({ ...edit, consentObtained: e.target.checked })} className="accent-[#C9A84C]" data-testid="tst-consent" /> Consent obtained (required to approve)</label>
          <button onClick={save} className="btn-gold w-full" data-testid="tst-save">Save</button>
        </Modal>
      )}
    </div>
  );
}
